import { XIcon } from '@langchain/untitled-ui-icons';

import { useState } from 'react';

import { ETemplateFormat } from '@/Pages/Playground/utils/types';
import { RichTextEditor } from '@/components/RichTextEditor/RichTextEditor';
import { getInputVarsFromTemplate } from '@/components/RichTextEditor/utils/RichTextEditor.utils';
import { IconButton, Text } from '@/design-system';
import { RunSchema } from '@/types/schema';
import { useLocalStorageState } from '@/utils/use-local-storage-state';

import {
  DEFAULT_SUMMARY_PROMPT,
  DISMISSED_SUMMARY_PROMPT_INSTRUCTIONS_LS_KEY,
} from '../constants';
import { useSuggestionsFromRunSchema } from '../hooks/useSuggestionsFromRunSchema';
import { InsightsJobFormData } from '../types';
import { isGenericVar } from '../utils/isGenericVar';
import { ErrorText, FormSection } from './ConfigSectionComponents';

const SummaryPromptInstructions = () => {
  const [
    dismissedSummaryPromptInstructions,
    setDismissedSummaryPromptInstructions,
  ] = useLocalStorageState(DISMISSED_SUMMARY_PROMPT_INSTRUCTIONS_LS_KEY, false);

  if (dismissedSummaryPromptInstructions) {
    return null;
  }

  return (
    <div className="grid grid-cols-[auto_1fr_auto] items-center gap-2 rounded-md bg-brand-green-50 px-3 py-2.5">
      <Text variant="md">
        Your prompt must include at least one variable e.g.{' '}
        <span className="font-mono font-semibold text-primary">
          {'{{run.inputs}}'}
        </span>
      </Text>
      <div className="flex justify-end">
        <IconButton
          label="Dismiss"
          size="sm"
          color="secondary"
          className="bg-brand-green-50"
          variant="plain"
          icon={XIcon}
          onClick={() => setDismissedSummaryPromptInstructions(true)}
        />
      </div>
    </div>
  );
};

const SummaryEditor = ({
  configKey,
  suggestions,
  summaryPrompt,
  onChange,
}: {
  configKey: string | null;
  suggestions: string[];
  summaryPrompt?: string;
  onChange: (prompt: string) => void;
}) => {
  const prompt = summaryPrompt || DEFAULT_SUMMARY_PROMPT;
  const variables = getInputVarsFromTemplate(prompt, ETemplateFormat.MUSTACHE);
  const [variableMappings, setVariableMappings] = useState(
    Object.fromEntries(
      variables.map((v) => {
        // In suggestions mode, leave generic variables unmapped so they show "Select path"
        return [v, isGenericVar(v) ? '' : v];
      })
    )
  );

  const updateVariableMapping = (newPrompt: string) => {
    const allVariables = getInputVarsFromTemplate(
      newPrompt,
      ETemplateFormat.MUSTACHE
    );

    const newVariableMappings = { ...variableMappings };
    // Add blank mappings for new variables if they're generic (ie. var1, var2, etc.)
    allVariables.forEach((v) => {
      if (!variableMappings[v]) {
        if (isGenericVar(v)) {
          newVariableMappings[v] = '';
        } else {
          newVariableMappings[v] = v;
        }
      }
    });
    // Remove mappings for variables that no longer exist in the prompt
    Object.keys(newVariableMappings).forEach((key) => {
      if (!allVariables.includes(key)) {
        delete newVariableMappings[key];
      }
    });
    setVariableMappings(newVariableMappings);
  };

  const onPromptChange = (newPrompt: string) => {
    updateVariableMapping(newPrompt);
    onChange(newPrompt);
  };

  return (
    <RichTextEditor
      key={configKey}
      initialValue={prompt}
      onChange={(e) => {
        if (typeof e === 'string') {
          onPromptChange(e);
        }
      }}
      variableMappingProps={{
        variableMappings,
        availablePaths: suggestions,
        uneditableVariables: [],
        setVariableMappings,
        setActiveVariable: () => {},
        storeType: 'summaryPrompt',
        isSuggestions: true,
      }}
      autoFocus={false}
      className="w-full resize-none rounded-sm border border-secondary bg-background p-3 text-sm focus:border-brand"
      templateFormat={ETemplateFormat.MUSTACHE}
      variant="template"
    />
  );
};

export const SummaryPromptSection = ({
  formData,
  latestRun,
  updateFormData,
  errors,
  configKey,
}: {
  formData: InsightsJobFormData;
  latestRun?: RunSchema;
  updateFormData: (updates: Partial<InsightsJobFormData>) => void;
  errors: Partial<Record<keyof InsightsJobFormData, string>>;
  configKey: string | null;
}) => {
  const suggestions = useSuggestionsFromRunSchema(latestRun);

  return (
    <FormSection
      title="Add a summary prompt"
      titleVariant="h2"
      description="Define a prompt to guide how summaries are generated. Each run will be summarized using this prompt, and those summaries will serve as the basis for generating insights."
    >
      <FormSection title="Summary Prompt">
        <SummaryPromptInstructions />
        <SummaryEditor
          configKey={configKey}
          suggestions={suggestions}
          summaryPrompt={formData.summaryPrompt}
          onChange={(prompt) => updateFormData({ summaryPrompt: prompt })}
        />
        <ErrorText error={errors.summaryPrompt} />
      </FormSection>
    </FormSection>
  );
};
